<template>
  <div class="contract">
    <div class="contract__header">
      <div class="f">
        <button
          class="btn hd ml-10"
          @click="emit('back')"
        >
          <i class="i-arrow rot_r mla light" />
        </button>
        <h2>{{ t("trade_up.title") }}</h2>
      </div>
      <button
        v-if="selectedItems.length"
        class="btn-clear"
        @click="clearItems"
      >
        {{ t("clear_all") }}
      </button>
    </div>
    <PagesTradeUpComponentsContractSideComponentsSelectedItems />

    <div
      v-if="selectedItems.length"
      class="contract__items-wrapper"
    >
      <div class="contract__items">
        <LazyPagesTradeUpComponentsContractSideComponentsPossibleCard
          v-for="item in possibleItems"
          :key="`${item.localed_name}-possibleItems`"
          :is-stattrak="false"
          :item="item"
          :percentage="item.percentage"
        />
      </div>
    </div>
    <div
      v-else
      class="contract_empty"
    >
      <div class="contract_empty__wrap">
        <img
          alt=""
          src="/img/tradeup-empty.svg"
        >
        <p>{{ t('trade_up.empty') }}</p>
        <span>
          {{ t('trade_up.empty_desc') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { TradeUpFullInfo, TradeUpSelectedItem } from '~/components/Pages/TradeUp/types';
import type { ItemPageInfo, ItemsByField } from '~/graphql/generated/schema-types';
import { useTradeUpStore } from '~/store/trade-up';
import { getItemsContainerPage, getTradeUpItemInfo } from '~/utils/api';

const emit = defineEmits(['back']);

const tradeUpStore = useTradeUpStore();
const { currentFullObject, possibleItems } = storeToRefs(tradeUpStore);
const selectedItems = computed({
  get: () => tradeUpStore.selectedItems,
  set: (value) => {
    tradeUpStore.selectedItems = value;
  },
});

const { t, locale } = useI18n();
const rarityOrder = [
  'Consumer Grade',
  'Industrial Grade',
  'Mil-Spec Grade',
  'Restricted',
  'Classified',
  'Covert',
]; // Указать порядок редкости

const containerItems = ref([] as ItemsByField[]);

watch(
  () => selectedItems.value,
  async (val, prev) => {
    if (val.length !== 10) {
      possibleItems.value = [];
      currentFullObject.value = {
        ...currentFullObject.value,
        cost: 0,
        market_hash_name: '',
        profit: 0,
      } as TradeUpFullInfo;
      return;
    }
    if (val.length === prev.length) {
      calculates();
    }
    await prepareItems();
  },
  { deep: true },
);

async function prepareItems() {
  const hasBadFloat = selectedItems.value.some(
    el => el.float < el.item_min_float || el.float > el.item_max_float,
  );
  if (hasBadFloat)
    return;
  const rarityUpLevel
      = rarityOrder.indexOf(selectedItems.value[0].rarity || 'Consumer Grade') + 1;

  containerItems.value = [] as ItemsByField[];
  const collections = [
    ...new Set(
      selectedItems.value.map(item =>
        getModifiedString(item.collection || ''),
      ),
    ),
  ];
  const response = await Promise.all(
    collections.map(i =>
      getItemsContainerPage(i || '', false, selectedItems.value[0].is_stattrak),
    ),
  );
  containerItems.value = response
    .map(i => (i.data?.itemsContainerPage?.result || []) as ItemsByField[])
    .flat();

  possibleItems.value = tradeUpStore.getPossibleItemsByItemsByField(
    containerItems.value
      .filter(i => i.rarity === rarityOrder[rarityUpLevel])
      .map(i => ({
        ...i,
        percentage: (0).toFixed(2),
        float: 0,
      })),
  );

  await nextTick(async () => {
    const res = await Promise.all(
      [
        ...new Set(
          possibleItems.value.map(item =>
            getModifiedString(item.market_hash_name),
          ),
        ),
      ].map(i => getTradeUpItemInfo(i || '', locale.value)),
    );
    const itemsObject = {} as Record<string, ItemPageInfo>;
    res.forEach((i) => {
      itemsObject[i.data?.itemPageInfo?.market_hash_name || '']
          = i.data?.itemPageInfo || {};
    });
    possibleItems.value = possibleItems.value.map(i => ({
      ...i,
      markets_info: itemsObject[i.market_hash_name]?.markets_info || [],
      volume: itemsObject[i.market_hash_name]?.volume || [],
    }));
  });

  calculates();
}

function calculates() {
  const selectedItemsCountInCollection = selectedItems.value.reduce(
    (acc, item) => {
      if (item.collection) {
        acc[item.collection] = acc[item.collection]
          ? acc[item.collection] + 1
          : 1;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  const possibleItemsCountInCollection = possibleItems.value.reduce(
    (acc, item) => {
      if (item.collection) {
        acc[item.collection] = acc[item.collection]
          ? acc[item.collection] + 1
          : 1;
      }

      return acc;
    },
    {} as Record<string, number>,
  );

  const allCollectionsCount = Object.keys(
    selectedItemsCountInCollection,
  ).reduce(
    (acc, key) =>
      acc
      + selectedItemsCountInCollection[key]
      * (possibleItemsCountInCollection[key] || 1),
    0,
  );

  possibleItems.value = possibleItems.value.map((i) => {
    if (i?.collection) {
      return {
        ...i,
        percentage:
            (100 / allCollectionsCount)
            * selectedItemsCountInCollection[i.collection],
        float: calculateTradeUpFloat(
          selectedItems.value,
          i.item_min_float || 0,
          i.item_max_float || 0,
        ),
      };
    }
    return i;
  });

  currentFullObject.value = {
    ...currentFullObject.value,
    market_hash_name:
        tradeUpStore.calculates?.mostPossibleItem?.market_hash_name || currentFullObject.value.market_hash_name || '',
    cost: tradeUpStore.calculates?.cost || currentFullObject.value.cost || 0,
    profit: tradeUpStore.calculates?.profit || currentFullObject.value.profit || 0,
  };
}

function calculateTradeUpFloat(
  items: TradeUpSelectedItem[], // список предметов (10 шт.)
  minFloat: number, // minFloat выпавшего скина
  maxFloat: number, // maxFloat выпавшего скина
): number {
  // 1. Считаем средний float среди 10 предметов
  const totalFloat = items.reduce((sum, item) => sum + (item.float > item.item_max_float ? item.item_max_float : item.float < item.item_min_float ? item.item_min_float : item.float), 0);
  const avgFloat = totalFloat / items.length; // обычно 10 предметов

  // 2. «Прожимаем» средний float в диапазон выпавшего скина
  // Возвращаем итоговое значение
  return avgFloat * (maxFloat - minFloat) + minFloat;
}

function clearItems() {
  selectedItems.value = [];
  currentFullObject.value = {
    ...currentFullObject.value,
    cost: 0,
    market_hash_name: '',
    profit: 0,
  } as TradeUpFullInfo;
  possibleItems.value = [];
}

onMounted(async () => {
  if (selectedItems.value.length === 10) {
    await calculates();
    await prepareItems();
  }
});
</script>

<style lang="scss" scoped>
@use "sass:color";

.contract {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    .btn-clear {
      border-radius: 10px;
      border: 1px solid var(--surfaces-outline);
      padding: 8px 12px;
      color: var(--destructive);
      text-align: center;
      font-size: 14px;
      font-weight: 600;
      line-height: 18px; /* 128.571% */
      &:hover {
        background: var(--surfaces-high-container);
      }
    }
  }

  &__items-wrapper {
    height: calc(100vh - 500px);
    overflow-y: auto;
    flex-grow: 1;
    @media screen and (max-width: 989px) {
      height: auto;
      max-height: initial;

    }
  }

  &__items {
    display: grid;
    align-items: flex-start;
    align-content: flex-start;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 12px;

    @media screen and (max-width: 989px) {
      grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
      height: auto;
      max-height: initial;
    }

    &::-webkit-scrollbar {
      width: 8px;
    }

    /* Трек */
    &::-webkit-scrollbar-track {
      border-radius: 10px;
      padding: 4px;
      background: transparent;
    }

    /* Ползунок */
    &::-webkit-scrollbar-thumb {
      background-color: var(--surfaces-outline);
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
      border-right: 0; // Убираем левую границу
      border-left: 4px solid transparent; // Оставляем правую границу для создания эффекта узкого ползунка
      border-radius: 4px;
      background-clip: padding-box; // Отсекает фон по области паддинга, сохраняя эффект отступа
    }

    /* Ползунок при наведении */
    &::-webkit-scrollbar-thumb:hover {
      background-color: color-mix(in srgb, var(--surfaces-outline) 95%, white);
    }
  }

  &_empty {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    &__wrap {
      width: 320px;
      text-align: center;

      p {
        color: var(--surfaces-on-surface);
        font-size: 18px;
        font-weight: 600;
        padding: 32px 0 8px;
      }

      span {
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 26px;
      }
    }
  }
}
</style>
